'use client';

import { forwardRef, useCallback, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { Slot } from '../slot';
import { ChipGroupContext } from './context/chip-group.context';
import { chipGroupRecipe } from './styles/chip-group.styles';
import type { ChipGroupPropsInterface } from './types/chip-group.types';

/**
 * `ChipGroup` is a component that renders a group of chips.
 *
 * @since 10.2.0
 */
export const ChipGroup = forwardRef<HTMLDivElement, ChipGroupPropsInterface>(
  (props: ChipGroupPropsInterface, forwardedRef) => {
    const {
      asChild,
      className,
      elevated,
      children,
      css: cssProp,
      selected,
      defaultSelected,
      onSelectionChange,
      multiple = false,
      disabled = false,
      verticalSpacing = 'default',
      role,
      ...rest
    } = props;

    const [selectedState, setSelectedState] = useControllableValue<
      string | Array<string> | undefined
    >({
      controlledValue: selected,
      uncontrolledValue: defaultSelected,
      isControlled: 'selected' in props,
      isUncontrolled: 'defaultSelected' in props,
      onChange: onSelectionChange,
      componentName: 'ChipGroup',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    const isSelected = useCallback(
      (value: string): boolean => {
        if (!selectedState) return false;

        if (multiple && Array.isArray(selectedState)) {
          return selectedState.includes(value);
        }

        return selectedState === value;
      },
      [selectedState, multiple],
    );

    const handleSelection = useCallback(
      (value: string) => {
        if (multiple) {
          const currentArray = Array.isArray(selectedState) ? selectedState : [];
          const isCurrentlySelected = currentArray.includes(value);

          const newSelection = isCurrentlySelected
            ? currentArray.filter((v) => v !== value)
            : [...currentArray, value];

          setSelectedState(newSelection);
        } else {
          // Single selection - toggle off if clicking the same value
          const newSelection = selectedState === value ? undefined : value;
          setSelectedState(newSelection);
        }
      },
      [selectedState, setSelectedState, multiple],
    );

    const contextValue = useMemo(
      () => ({
        selected: selectedState,
        setSelected: setSelectedState,
        multiple,
        disabled,
        elevated,
        isSelected,
        handleSelection,
      }),
      [selectedState, setSelectedState, multiple, disabled, elevated, isSelected, handleSelection],
    );

    const styles = css(chipGroupRecipe.raw({ verticalSpacing }), cssProp);

    const Component = asChild ? Slot : 'div';

    const resolvedRole = asChild ? role : (role ?? 'group');

    return (
      <ChipGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ChipGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_chip-group', styles, className)}
          role={resolvedRole}
          {...rest}
        >
          {children}
        </Component>
      </ChipGroupContext.Provider>
    );
  },
);

ChipGroup.displayName = 'ChipGroup';
